import type { IsoProjection } from './IsoProjection.js';
import type { Grid } from './Grid.js';
import type { ScreenPoint, WorldPoint } from '../types.js';

/**
 * The floor Renderer's BACKDROP_MARGIN (1 / MIN_ZOOM) was sized for: below
 * this, the cover-fit backdrop no longer fills the viewport.
 */
export const MIN_ZOOM = 0.8;
export const MAX_ZOOM = 2.5;

export class Camera {
    target: WorldPoint = { x: 0, z: 0 };
    width = 0;
    height = 0;
    zoom = 1;
    private dpr = 1;

    constructor(
        private readonly canvas: HTMLCanvasElement,
        private readonly ctx: CanvasRenderingContext2D,
        private readonly iso: IsoProjection,
        private readonly grid: Grid,
    ) {}

    worldToCanvas(x: number, z: number): ScreenPoint {
        const point = this.iso.worldToScreen(x, z);
        const origin = this.iso.worldToScreen(this.target.x, this.target.z);
        return {
            x: this.width / 2 + (point.sx - origin.sx) * this.zoom,
            y: this.height / 2 + (point.sy - origin.sy) * this.zoom,
        };
    }

    canvasToWorld(px: number, py: number): WorldPoint {
        const origin = this.iso.worldToScreen(this.target.x, this.target.z);
        return this.iso.screenToWorld(
            (px - this.width / 2) / this.zoom + origin.sx,
            (py - this.height / 2) / this.zoom + origin.sy,
        );
    }

    panByScreenDelta(dx: number, dy: number): void {
        const delta = this.iso.screenToWorld(-dx / this.zoom, -dy / this.zoom);
        this.target.x += delta.x;
        this.target.z += delta.z;
        this.clampTarget();
    }

    /**
     * Set the zoom keeping the world point currently under screen (px, py)
     * pinned to the cursor — the standard zoom-to-pointer behaviour.
     */
    setZoomAt(px: number, py: number, nextZoom: number): void {
        const clamped = Math.min(Math.max(nextZoom, MIN_ZOOM), MAX_ZOOM);
        if (clamped === this.zoom) return;

        const anchor = this.canvasToWorld(px, py);
        this.zoom = clamped;

        const anchorScreen = this.iso.worldToScreen(anchor.x, anchor.z);
        const originScreenX = anchorScreen.sx - (px - this.width / 2) / this.zoom;
        const originScreenY = anchorScreen.sy - (py - this.height / 2) / this.zoom;
        const originWorld = this.iso.screenToWorld(originScreenX, originScreenY);

        this.target.x = originWorld.x;
        this.target.z = originWorld.z;
        this.clampTarget();
    }

    private clampTarget(): void {
        const halfW = this.grid.worldWidth / 2;
        const halfH = this.grid.worldHeight / 2;
        this.target.x = Math.min(Math.max(this.target.x, -halfW), halfW);
        this.target.z = Math.min(Math.max(this.target.z, -halfH), halfH);
    }

    applySize(): boolean {
        const width = this.canvas.clientWidth || 0;
        const height = this.canvas.clientHeight || 0;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        if (width === this.width && height === this.height && dpr === this.dpr) return false;

        this.width = width;
        this.height = height;
        this.dpr = dpr;

        this.canvas.width = Math.round(width * dpr);
        this.canvas.height = Math.round(height * dpr);
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        return true;
    }
}
